class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Cart Drawer Popup Handler
class CartDrawerPopup {
  constructor() {
    this.shippingPopup = document.getElementById('drawer-shipping-popup');
    this.orderNotePopup = document.getElementById('drawer-order-note-popup');
    this.shippingBtn = document.getElementById('drawer-estimate-shipping-btn');
    this.orderNoteBtn = document.getElementById('drawer-add-order-note-btn');

    // Shipping popup elements
    this.closeShippingBtn = document.getElementById('close-drawer-shipping');
    this.shippingOverlay = document.getElementById('drawer-shipping-overlay');
    this.shippingForm = document.getElementById('drawer-shipping-form');
    this.shippingResults = document.getElementById('drawer-shipping-results');
    this.shippingErrorResults = document.getElementById('drawer-shipping-results-error');
    this.estimateBtnLoader = document.getElementById('drawer-estimate-btn-loader');
    this.getEstimateBtn = document.getElementById('drawer-get-estimate-btn');
    this.countrySelect = document.getElementById('drawer-shipping-country');
    this.provinceField = document.getElementById('drawer-shipping-province-field');
    this.provinceSelect = document.getElementById('drawer-shipping-province');
    this.provinceLabel = document.getElementById('drawer-shipping-province-label');
    this.zipLabel = document.getElementById('drawer-shipping-zip-label');
    this.zipInput = document.getElementById('drawer-shipping-zip');

    // Order note elements
    this.closeOrderNoteBtn = document.getElementById('close-drawer-order-note');
    this.orderNoteOverlay = document.getElementById('drawer-order-note-overlay');
    this.orderNoteTextarea = document.getElementById('CartDrawer-Note');

    this.shippingCountries = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.fetchShippingCountries();
  }

  bindEvents() {
    // Shipping popup events
    this.shippingBtn?.addEventListener('click', () => this.openShippingPopup());
    this.closeShippingBtn?.addEventListener('click', () => this.closeShippingPopup());
    this.shippingOverlay?.addEventListener('click', () => this.closeShippingPopup());
    this.shippingForm?.addEventListener('submit', (e) => this.handleShippingSubmit(e));
    this.countrySelect?.addEventListener('change', () => this.handleCountryChange());

    // Order note popup events
    this.orderNoteBtn?.addEventListener('click', () => this.openOrderNotePopup());
    this.closeOrderNoteBtn?.addEventListener('click', () => this.closeOrderNotePopup());
    this.orderNoteOverlay?.addEventListener('click', () => this.closeOrderNotePopup());

    // Close popups on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeShippingPopup();
        this.closeOrderNotePopup();
      }
    });
  }

  // Shipping Popup Methods
  openShippingPopup() {
    this.shippingPopup?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeShippingPopup() {
    this.shippingPopup?.classList.remove('active');
    document.body.style.overflow = '';
    if (this.shippingResults) this.shippingResults.innerHTML = '';
    if (this.shippingErrorResults) this.shippingErrorResults.innerHTML = '';

    const countryError = document.getElementById('drawer-country-error');
    const zipError = document.getElementById('drawer-zip-error');
    if (countryError) countryError.textContent = '';
    if (zipError) zipError.textContent = '';
  }

  // Order Note Popup Methods
  openOrderNotePopup() {
    this.orderNotePopup?.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.orderNoteTextarea?.focus();
  }

  closeOrderNotePopup() {
    this.orderNotePopup?.classList.remove('active');
    document.body.style.overflow = '';
  }

  // Shipping Countries and Form Handling
  async fetchShippingCountries() {
    try {
      const localeResponse = await fetch('/services/countries.js');
      if (!localeResponse.ok) {
        console.warn('Could not fetch countries list, using default dropdown');
        return;
      }

      const responseText = await localeResponse.text();

      try {
        const jsonString = responseText.replace(/^var Countries\s*=\s*/, '').replace(/;$/, '');
        const allCountries = JSON.parse(jsonString);

        if (!allCountries) {
          console.warn('Could not parse countries data');
          return;
        }

        const localizationResponse = await fetch('/?section_id=header');

        if (localizationResponse.ok) {
          const htmlText = await localizationResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText, 'text/html');
          const countryOptions = doc.querySelectorAll('localization-form select[name="country_code"] option, form[action*="localization"] select[name="country_code"] option');

          if (countryOptions.length > 0) {
            const availableCountryCodes = new Set();
            countryOptions.forEach(option => {
              const code = option.value;
              if (code && code !== '') {
                availableCountryCodes.add(code);
              }
            });

            const marketCountries = {};
            Object.entries(allCountries).forEach(([name, data]) => {
              if (availableCountryCodes.has(data.code)) {
                marketCountries[name] = data;
              }
            });

            if (Object.keys(marketCountries).length > 0) {
              this.shippingCountries = marketCountries;
              this.populateCountryDropdown();
              return;
            }
          }
        }

        console.warn('Could not fetch market countries, using all available countries');
        this.shippingCountries = allCountries;
        this.populateCountryDropdown();

      } catch (parseError) {
        console.error('Error parsing countries data:', parseError);
      }
    } catch (error) {
      console.error('Error fetching shipping countries:', error);
    }
  }

  populateCountryDropdown() {
    if (!this.countrySelect || !this.shippingCountries) return;

    this.countrySelect.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = 'Select a country';
    this.countrySelect.appendChild(placeholderOption);

    Object.entries(this.shippingCountries).forEach(([name, country]) => {
      const option = document.createElement('option');
      option.value = country.code;
      option.textContent = name;

      if (country.code === 'AU' || name === 'Australia') {
        option.selected = true;
      }

      this.countrySelect.appendChild(option);
    });

    this.handleCountryChange();
  }

  handleCountryChange() {
    const selectedCountryCode = this.countrySelect?.value;
    if (!selectedCountryCode || !this.shippingCountries) {
      this.hideProvinceField();
      return;
    }

    const countryData = Object.values(this.shippingCountries).find(
      country => country.code === selectedCountryCode
    );

    if (!countryData) {
      this.hideProvinceField();
      return;
    }

    if (this.zipLabel && countryData.zip_label) {
      this.zipLabel.textContent = countryData.zip_label;
    }
    if (this.zipInput && countryData.zip_placeholder) {
      this.zipInput.placeholder = countryData.zip_placeholder;
    }

    if (countryData.provinces && countryData.provinces.length > 0) {
      this.showProvinceField(countryData);
    } else {
      this.hideProvinceField();
    }
  }

  showProvinceField(countryData) {
    if (!this.provinceField || !this.provinceSelect) return;

    if (this.provinceLabel && countryData.province_label) {
      this.provinceLabel.textContent = countryData.province_label;
    }

    this.provinceSelect.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = `Select ${countryData.province_label || 'province'}`;
    this.provinceSelect.appendChild(placeholder);

    countryData.provinces.forEach(provinceName => {
      const option = document.createElement('option');
      option.value = countryData.province_codes?.[provinceName] || provinceName;
      option.textContent = provinceName;
      this.provinceSelect.appendChild(option);
    });

    this.provinceField.style.display = 'block';
    this.provinceSelect.required = true;
  }

  hideProvinceField() {
    if (!this.provinceField || !this.provinceSelect) return;

    this.provinceField.style.display = 'none';
    this.provinceSelect.required = false;
    this.provinceSelect.value = '';
  }

  async handleShippingSubmit(e) {
    e.preventDefault();

    const country = this.countrySelect?.value;
    const province = this.provinceSelect?.value;
    const zip = this.zipInput?.value;
    const countryError = document.getElementById('drawer-country-error');
    const provinceError = document.getElementById('drawer-province-error');
    const zipError = document.getElementById('drawer-zip-error');

    if (countryError) countryError.textContent = '';
    if (provinceError) provinceError.textContent = '';
    if (zipError) zipError.textContent = '';

    let hasError = false;
    if (!country) {
      if (countryError) countryError.textContent = 'Please select a country';
      hasError = true;
    }
    if (this.provinceSelect?.required && !province) {
      if (provinceError) provinceError.textContent = 'Please select a state/province';
      hasError = true;
    }
    if (!zip) {
      if (zipError) zipError.textContent = 'Please enter a ZIP/postal code';
      hasError = true;
    }

    if (hasError) return;

    this.showButtonLoader();

    try {
      await this.fetchShippingRates(country, province, zip);
    } catch (error) {
      console.error('Shipping calculation error:', error);
      if (this.shippingResults) {
        this.shippingResults.innerHTML = '';
        this.shippingErrorResults.innerHTML = '<div class="message message-danger text-danger">' + error + '</div>';
      }
    } finally {
      this.hideButtonLoader();
    }
  }

  async fetchShippingRates(country, province, zip) {
    const shippingAddress = {
      zip: zip,
      country: country,
      province: province || ''
    };

    const prepareResponse = await fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ shipping_address: shippingAddress })
    });

    if (!prepareResponse.ok) {
      let error = 'Unable to prepare shipping calculation';
      const response = await prepareResponse.json();
      const isZip = response?.zip;
      if (isZip?.length) error = isZip[0];
      throw new Error(error);
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    const ratesResponse = await fetch('/cart/async_shipping_rates.json', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!ratesResponse.ok) {
      throw new Error('Unable to fetch shipping rates');
    }

    const ratesData = await ratesResponse.json();

    if (ratesData.rates === undefined) {
      await new Promise(resolve => setTimeout(resolve, 1500));

      const retryResponse = await fetch('/cart/async_shipping_rates.json', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (retryResponse.ok) {
        const retryData = await retryResponse.json();
        this.displayShippingRates(retryData);
      } else {
        throw new Error('Unable to fetch shipping rates');
      }
    } else {
      this.displayShippingRates(ratesData);
    }
  }

  displayShippingRates(data) {
    if (!this.shippingResults) return;

    if (data.shipping_rates && data.shipping_rates.length > 0) {
      let html = '<div class="shipping-rates"><ul class="shipping-rates-list">';

      data.shipping_rates.forEach(rate => {
        const price = parseFloat(rate.price);
        const formattedPrice = this.formatMoney(price);

        html += `<li class="shipping-rate-item">
          <span class="shipping-rate-name">${this.escapeHtml(rate.name)}</span>
          <span class="shipping-rate-price">${formattedPrice}</span>
        </li>`;
      });

      html += '</ul></div>';
      this.shippingResults.innerHTML = html;
    } else {
      this.shippingResults.innerHTML = '<div class="shipping-no-rates">No shipping rates available for this location.</div>';
    }
  }

  formatMoney(amount) {
    const cents = parseFloat(amount);
    const value = cents > 1000 ? cents / 100 : cents;

    const currency = window.Shopify?.currency?.active || 'USD';

    let locale = 'en-US';
    if (currency === 'AUD') locale = 'en-AU';
    else if (currency === 'NZD') locale = 'en-NZ';
    else if (currency === 'GBP') locale = 'en-GB';
    else if (currency === 'EUR') locale = 'en-IE';

    try {
      if (currency === 'AUD' || currency === 'NZD') {
        return `${value.toFixed(2)} ${currency}`;
      }

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    } catch (error) {
      return `${value.toFixed(2)} ${currency}`;
    }
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  showButtonLoader() {
    if (this.estimateBtnLoader) {
      this.estimateBtnLoader.style.display = 'flex';
    }
    if (this.getEstimateBtn) {
      this.getEstimateBtn.disabled = true;
      this.getEstimateBtn.style.opacity = '0.6';
    }
  }

  hideButtonLoader() {
    if (this.estimateBtnLoader) {
      this.estimateBtnLoader.style.display = 'none';
    }
    if (this.getEstimateBtn) {
      this.getEstimateBtn.disabled = false;
      this.getEstimateBtn.style.opacity = '1';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerPopup();
  });
} else {
  new CartDrawerPopup();
}
